import React, { useEffect, useState } from "react";
import { Linkedin, Facebook, Instagram, Store, Heart, MessageCircle, Repeat2, Send, ThumbsUp, Globe, MoreHorizontal, Bookmark } from "lucide-react";

const NETS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "google", label: "Google", icon: Store },
];

const Avatar = ({ size = 40 }) => (
  <div style={{ width: size, height: size }} className="rounded-full bg-navy text-white flex items-center justify-center font-extrabold shrink-0" data-testid="preview-avatar">
    <span style={{ fontSize: size * 0.4 }}>C</span>
  </div>
);

const Img = ({ url }) => (
  url ? <img src={url} alt="post" className="w-full max-h-64 object-cover" data-testid="preview-image" /> : null
);

// A LinkedIn/Facebook-style link-preview card (used when no image is attached
// but the caption contains a link). Mirrors what the network will unfurl.
const LinkCard = ({ lp }) => {
  if (!lp) return null;
  let domain = lp.site || "";
  try { domain = new URL(lp.url).hostname.replace(/^www\./, ""); } catch (e) {}
  return (
    <div className="border-t border-slate-100" data-testid="preview-linkcard">
      <div className="flex items-stretch">
        <div className="w-28 shrink-0 bg-slate-100 overflow-hidden flex items-center justify-center">
          {lp.image
            ? <img src={lp.image} alt="" className="w-full h-full object-cover" data-testid="preview-linkcard-image" />
            : <span className="text-[10px] text-slate-400 p-2 text-center">No preview image</span>}
        </div>
        <div className="p-3 min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-400">{domain}</p>
          <p className="text-sm font-bold text-slate-800 line-clamp-2 mt-0.5">{lp.title || lp.url}</p>
          {lp.description && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{lp.description}</p>}
        </div>
      </div>
    </div>
  );
};

// A card shows the attached image if present, otherwise the unfurled link card.
const Media = ({ image, lp }) => (image ? <Img url={image} /> : <LinkCard lp={lp} />);

function LinkedInCard({ text, image, lp }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="preview-linkedin">
      <div className="p-3 flex items-start gap-2">
        <Avatar />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 leading-tight">CASS <span className="font-normal text-slate-400">· 1st</span></p>
          <p className="text-xs text-slate-500">More than numbers · Now · <Globe size={11} className="inline" /></p>
        </div>
        <MoreHorizontal size={18} className="text-slate-400" />
      </div>
      <p className="px-3 pb-3 text-sm text-slate-800 whitespace-pre-wrap break-words">{text || "Your caption will appear here…"}</p>
      <Media image={image} lp={lp} />
      <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500 text-xs font-semibold">
        <span className="flex items-center gap-1"><ThumbsUp size={14} /> Like</span>
        <span className="flex items-center gap-1"><MessageCircle size={14} /> Comment</span>
        <span className="flex items-center gap-1"><Repeat2 size={14} /> Repost</span>
        <span className="flex items-center gap-1"><Send size={14} /> Send</span>
      </div>
    </div>
  );
}

function FacebookCard({ text, image, lp }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="preview-facebook">
      <div className="p-3 flex items-start gap-2">
        <Avatar />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 leading-tight">CASS</p>
          <p className="text-xs text-slate-500">Just now · <Globe size={11} className="inline" /></p>
        </div>
        <MoreHorizontal size={18} className="text-slate-400" />
      </div>
      <p className="px-3 pb-3 text-sm text-slate-800 whitespace-pre-wrap break-words">{text || "Your caption will appear here…"}</p>
      <Media image={image} lp={lp} />
      <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500 text-xs font-semibold">
        <span className="flex items-center gap-1"><ThumbsUp size={14} /> Like</span>
        <span className="flex items-center gap-1"><MessageCircle size={14} /> Comment</span>
        <span className="flex items-center gap-1"><Send size={14} /> Share</span>
      </div>
    </div>
  );
}

function InstagramCard({ text, image }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="preview-instagram">
      <div className="p-3 flex items-center gap-2">
        <Avatar size={32} />
        <p className="text-sm font-bold text-slate-900">cass_online</p>
        <MoreHorizontal size={18} className="text-slate-400 ml-auto" />
      </div>
      {image
        ? <Img url={image} />
        : <div className="w-full h-56 bg-slate-100 flex items-center justify-center text-slate-400 text-xs" data-testid="preview-ig-noimage">Instagram needs an image</div>}
      <div className="px-3 pt-2 flex items-center gap-4 text-slate-800">
        <Heart size={20} /><MessageCircle size={20} /><Send size={20} /><Bookmark size={20} className="ml-auto" />
      </div>
      <p className="px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words"><span className="font-bold mr-1">cass_online</span>{text || "Your caption will appear here…"}</p>
    </div>
  );
}

function GoogleCard({ text, image, lp }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="preview-google">
      <div className="p-3 flex items-center gap-2">
        <Avatar size={32} />
        <div>
          <p className="text-sm font-bold text-slate-900 leading-tight">CASS</p>
          <p className="text-xs text-slate-500">Google Business · Update</p>
        </div>
      </div>
      <Media image={image} lp={lp} />
      <p className="px-3 py-3 text-sm text-slate-800 whitespace-pre-wrap break-words">{text || "Your update will appear here…"}</p>
      <div className="px-3 pb-3"><span className="text-xs font-bold text-blue-600">Learn more →</span></div>
    </div>
  );
}

const CARD = { linkedin: LinkedInCard, facebook: FacebookCard, instagram: InstagramCard, google: GoogleCard };

// Lightweight per-network preview panel. Shows a tab for each selected network.
// `linkPreview` (optional) is the fetched OG data used when no image is attached.
export default function NetworkPreview({ text, image, networks, linkPreview }) {
  const active = NETS.filter((n) => networks.includes(n.key));
  const [tab, setTab] = useState(active[0]?.key || "linkedin");

  useEffect(() => {
    if (active.length && !active.find((n) => n.key === tab)) setTab(active[0].key);
  }, [networks]); // eslint-disable-line

  if (!active.length) return null;
  const Card = CARD[tab] || LinkedInCard;

  return (
    <div data-testid="network-preview">
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        {active.map((n) => {
          const Icon = n.icon;
          const on = tab === n.key;
          return (
            <button key={n.key} type="button" data-testid={`preview-tab-${n.key}`} onClick={() => setTab(n.key)}
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-navy/30"}`}>
              <Icon size={12} /> {n.label}
            </button>
          );
        })}
      </div>
      <div className="bg-slate-50 rounded-xl p-3">
        <Card text={text} image={image} lp={linkPreview} />
      </div>
    </div>
  );
}
